import { useState } from 'react';
import {
  Users,
  Search,
  Mail,
  Shield,
  ShieldCheck,
  UserPlus,
  Edit3,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  Download,
  ChevronLeft,
  ChevronRight,
  Save,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';

type UserRole = 'admin' | 'moderator' | 'user';
type UserStatus = 'active' | 'inactive' | 'suspended';
type UserPlan = 'free' | 'pro' | 'enterprise';

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: UserRole;
  status: UserStatus;
  plan: UserPlan;
  downloadsCount: number;
  extensionsCount: number;
  createdAt: Date;
  lastLogin?: Date;
}

// Demo data
const initialUsers: User[] = [
  {
    id: '1',
    name: 'Ahmet Yilmaz',
    email: 'ahmet@example.com',
    role: 'admin',
    status: 'active',
    plan: 'enterprise',
    downloadsCount: 156,
    extensionsCount: 12,
    createdAt: new Date('2023-06-15'),
    lastLogin: new Date('2024-01-18'),
  },
  {
    id: '2',
    name: 'Fatma Demir',
    email: 'fatma@example.com',
    role: 'moderator',
    status: 'active',
    plan: 'pro',
    downloadsCount: 89,
    extensionsCount: 5,
    createdAt: new Date('2023-08-20'),
    lastLogin: new Date('2024-01-17'),
  },
  {
    id: '3',
    name: 'Mehmet Kaya',
    email: 'mehmet@example.com',
    role: 'user',
    status: 'active',
    plan: 'free',
    downloadsCount: 23,
    extensionsCount: 2,
    createdAt: new Date('2023-10-01'),
    lastLogin: new Date('2024-01-15'),
  },
  {
    id: '4',
    name: 'Zeynep Arslan',
    email: 'zeynep@example.com',
    role: 'user',
    status: 'inactive',
    plan: 'pro',
    downloadsCount: 45,
    extensionsCount: 3,
    createdAt: new Date('2023-09-10'),
    lastLogin: new Date('2024-01-10'),
  },
  {
    id: '5',
    name: 'Can Ozturk',
    email: 'can@example.com',
    role: 'user',
    status: 'suspended',
    plan: 'free',
    downloadsCount: 12,
    extensionsCount: 0,
    createdAt: new Date('2023-11-05'),
  },
  {
    id: '6',
    name: 'Elif Sahin',
    email: 'elif@example.com',
    role: 'user',
    status: 'active',
    plan: 'enterprise',
    downloadsCount: 234,
    extensionsCount: 18,
    createdAt: new Date('2023-07-22'),
    lastLogin: new Date('2024-01-18'),
  },
  {
    id: '7',
    name: 'Burak Yildiz',
    email: 'burak@example.com',
    role: 'moderator',
    status: 'active',
    plan: 'pro',
    downloadsCount: 67,
    extensionsCount: 4,
    createdAt: new Date('2023-12-01'),
    lastLogin: new Date('2024-01-16'),
  },
  {
    id: '8',
    name: 'Selin Aksoy',
    email: 'selin@example.com',
    role: 'user',
    status: 'active',
    plan: 'free',
    downloadsCount: 8,
    extensionsCount: 1,
    createdAt: new Date('2024-01-05'),
    lastLogin: new Date('2024-01-14'),
  },
];

export function AdminUsers() {
  const [users, setUsers] = useState<User[]>(initialUsers);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState<UserRole>('user');
  const [formPlan, setFormPlan] = useState<UserPlan>('free');
  const [formStatus, setFormStatus] = useState<UserStatus>('active');

  // Calculate stats
  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    paid: users.filter(u => u.plan === 'pro' || u.plan === 'enterprise').length,
    staff: users.filter(u => u.role === 'admin' || u.role === 'moderator').length,
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <ShieldCheck className="text-red-500" size={14} />;
      case 'moderator': return <Shield className="text-orange-500" size={14} />;
      default: return <Users className="text-text-tertiary" size={14} />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">
          <CheckCircle2 size={10} /> Aktif
        </span>;
      case 'inactive':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-500/10 text-gray-500">
          <Clock size={10} /> Pasif
        </span>;
      case 'suspended':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-500">
          <XCircle size={10} /> Askiya Alindi
        </span>;
      default:
        return null;
    }
  };

  const getPlanBadge = (plan: string) => {
    const colors = {
      free: 'bg-gray-500/10 text-gray-500',
      pro: 'bg-accent/10 text-accent',
      enterprise: 'bg-purple-500/10 text-purple-500'
    };
    const labels = {
      free: 'Ucretsiz',
      pro: 'Pro',
      enterprise: 'Kurumsal'
    };
    return (
      <span className={cn('text-xs px-2 py-0.5 rounded-full capitalize', colors[plan as keyof typeof colors])}>
        {labels[plan as keyof typeof labels]}
      </span>
    );
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === paginatedUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(paginatedUsers.map(u => u.id));
    }
  };

  const handleAddUser = () => {
    if (!formName || !formEmail) return;

    const newUser: User = {
      id: Date.now().toString(),
      name: formName,
      email: formEmail,
      role: formRole,
      status: formStatus,
      plan: formPlan,
      downloadsCount: 0,
      extensionsCount: 0,
      createdAt: new Date(),
    };

    setUsers([newUser, ...users]);
    resetForm();
    setShowAddModal(false);
  };

  const handleUpdateUser = () => {
    if (!editingUser) return;

    setUsers(users.map(u =>
      u.id === editingUser.id
        ? { ...u, name: formName, email: formEmail, role: formRole, status: formStatus, plan: formPlan }
        : u
    ));
    resetForm();
    setEditingUser(null);
  };

  const handleDeleteUser = (id: string) => {
    if (confirm('Bu kullaniciyi silmek istediginizden emin misiniz?')) {
      setUsers(users.filter(u => u.id !== id));
    }
  };

  const handleEditUser = (user: User) => {
    setFormName(user.name);
    setFormEmail(user.email);
    setFormRole(user.role);
    setFormStatus(user.status);
    setFormPlan(user.plan);
    setEditingUser(user);
  };

  const resetForm = () => {
    setFormName('');
    setFormEmail('');
    setFormRole('user');
    setFormStatus('active');
    setFormPlan('free');
  };

  const handleBulkAction = (action: 'activate' | 'suspend' | 'delete') => {
    if (selectedUsers.length === 0) return;

    if (action === 'delete') {
      if (confirm(`${selectedUsers.length} kullaniciyi silmek istediginizden emin misiniz?`)) {
        setUsers(users.filter(u => !selectedUsers.includes(u.id)));
        setSelectedUsers([]);
      }
    } else {
      const status = action === 'activate' ? 'active' : 'suspended';
      setUsers(users.map(u =>
        selectedUsers.includes(u.id) ? { ...u, status } : u
      ));
      setSelectedUsers([]);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['ID', 'Ad Soyad', 'E-posta', 'Rol', 'Durum', 'Plan', 'Kayit Tarihi'].join(','),
      ...users.map(u => [
        u.id,
        u.name,
        u.email,
        u.role,
        u.status,
        u.plan,
        u.createdAt.toISOString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kullanicilar-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Kullanicilar</h1>
          <p className="text-text-secondary">Kullanici hesaplarini ve izinlerini yonetin</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary hover:border-accent transition-colors"
          >
            <Download size={16} /> Disa Aktar
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors"
          >
            <UserPlus size={16} /> Kullanici Ekle
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-accent">{stats.total}</p>
          <p className="text-sm text-text-secondary">Toplam Kullanici</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-500">{stats.active}</p>
          <p className="text-sm text-text-secondary">Aktif</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-purple-500">{stats.paid}</p>
          <p className="text-sm text-text-secondary">Odeme Yapan</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-orange-500">{stats.staff}</p>
          <p className="text-sm text-text-secondary">Ekip Uyesi</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-bg-card border border-border-default rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Kullanici ara..."
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
          >
            <option value="all">Tum Roller</option>
            <option value="admin">Admin</option>
            <option value="moderator">Moderator</option>
            <option value="user">Kullanici</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
          >
            <option value="all">Tum Durumlar</option>
            <option value="active">Aktif</option>
            <option value="inactive">Pasif</option>
            <option value="suspended">Askiya Alindi</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border-default">
            <span className="text-sm text-text-secondary">{selectedUsers.length} secili</span>
            <button
              onClick={() => handleBulkAction('activate')}
              className="px-3 py-1.5 rounded-lg bg-green-500/10 text-green-500 text-sm hover:bg-green-500/20"
            >
              Aktif Et
            </button>
            <button
              onClick={() => handleBulkAction('suspend')}
              className="px-3 py-1.5 rounded-lg bg-yellow-500/10 text-yellow-500 text-sm hover:bg-yellow-500/20"
            >
              Askiya Al
            </button>
            <button
              onClick={() => handleBulkAction('delete')}
              className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-500 text-sm hover:bg-red-500/20"
            >
              Sil
            </button>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-bg-elevated border-b border-border-default">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === paginatedUsers.length && paginatedUsers.length > 0}
                  onChange={toggleAllUsers}
                  className="w-4 h-4 rounded border-border-default"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-text-tertiary uppercase">Kullanici</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-text-tertiary uppercase">Rol</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-text-tertiary uppercase">Durum</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-text-tertiary uppercase">Plan</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-text-tertiary uppercase">Indirme</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-text-tertiary uppercase">Kayit Tarihi</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-text-tertiary uppercase">Islemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <Users className="mx-auto mb-4 text-text-tertiary" size={48} />
                  <p className="text-text-tertiary">Kullanici bulunamadi</p>
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-bg-elevated/50 transition-colors">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      className="w-4 h-4 rounded border-border-default"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          user.name.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">{user.name}</p>
                        <p className="text-xs text-text-tertiary">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {getRoleIcon(user.role)}
                      <span className="text-sm text-text-primary capitalize">{user.role}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(user.status)}</td>
                  <td className="px-4 py-3">{getPlanBadge(user.plan)}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {user.downloadsCount}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {user.createdAt.toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button className="p-1.5 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-accent">
                        <Mail size={16} />
                      </button>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="p-1.5 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-accent"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="p-1.5 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border-default">
            <p className="text-sm text-text-tertiary">
              {paginatedUsers.length} / {filteredUsers.length} kullanici gosteriliyor
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-bg-elevated hover:bg-bg-elevated/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={cn(
                    'px-3 py-1 rounded-lg text-sm',
                    currentPage === page ? 'bg-accent text-white' : 'bg-bg-elevated hover:bg-bg-elevated/80'
                  )}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-2 rounded-lg bg-bg-elevated hover:bg-bg-elevated/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit User Modal */}
      {(showAddModal || editingUser) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-border-default flex items-center justify-between">
              <h2 className="text-xl font-semibold text-text-primary">
                {editingUser ? 'Kullanici Duzenle' : 'Yeni Kullanici Ekle'}
              </h2>
              <button
                onClick={() => { setShowAddModal(false); setEditingUser(null); resetForm(); }}
                className="p-1 hover:bg-bg-elevated rounded-lg text-text-tertiary"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Ad Soyad</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ahmet Yilmaz"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">E-posta</label>
                <input
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  placeholder="ahmet@example.com"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Rol</label>
                  <select
                    value={formRole}
                    onChange={(e) => setFormRole(e.target.value as UserRole)}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  >
                    <option value="user">Kullanici</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Plan</label>
                  <select
                    value={formPlan}
                    onChange={(e) => setFormPlan(e.target.value as UserPlan)}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  >
                    <option value="free">Ucretsiz</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Kurumsal</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Durum</label>
                <select
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.target.value as UserStatus)}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                  <option value="suspended">Askiya Alindi</option>
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-border-default flex justify-end gap-3">
              <button
                onClick={() => { setShowAddModal(false); setEditingUser(null); resetForm(); }}
                className="px-4 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary transition-colors"
              >
                Iptal
              </button>
              <button
                onClick={editingUser ? handleUpdateUser : handleAddUser}
                disabled={!formName || !formEmail}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {editingUser ? <Save size={16} /> : <UserPlus size={16} />}
                {editingUser ? 'Kaydet' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
